import {
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
  SecurityScanOutlined,
} from '@ant-design/icons'
import { Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import * as auth from 'src/app/modules/auth'
import * as userRedux from 'src/app/modules/userMgmt'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import { ThemeModeSelector } from 'src/cms/layout/components/header/ThemeModeSelector'
import * as Yup from 'yup'
import ChangePassword from './SetPassword'
import './profile.css'

const FORM_VALIDATION = Yup.object().shape({
  mobile_number: Yup.string()
    .matches(/^(98|97)([0-9]{8})$/, 'Enter valid mobile number')
    .max(10, 'Mobile number should not exceed 10 digits')
    .required('Mobile number is required.'),
})
const ProfileHeader: React.FC = () => {
  const authResp = useSelector((state: any) => state.auth)
  const { loading, success } = useSelector((state: any) => state.userManagement)
  const { user } = authResp
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const handleClose = () => setOpen(false)
  useEffect(() => {
    if (success) {
      dispatch(auth.actions.requestUser())
      toast.success('Profile updated successfully')
    }
  }, [success])

  const name: string = user?.data?.name ?? ''
  const initials =
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]?.toUpperCase())
      .join('') || 'U'

  const contactChips = [
    { key: 'phone', icon: <PhoneOutlined />, value: user?.data?.mobile_number },
    { key: 'email', icon: <MailOutlined />, value: user?.data?.email },
    { key: 'branch', icon: <BankOutlined />, value: user?.data?.branch?.title },
  ].filter((chip) => !!chip.value)

  return (
    <div className='amnil-profile'>
      {/* ── Banner: same navy + Amnil glow as the dashboard hero ── */}
      <section className='amnil-profile__hero'>
        <div className='amnil-profile__identity'>
          <div className='amnil-profile__avatar' aria-hidden='true'>
            {initials}
          </div>
          <div className='amnil-profile__who'>
            <div className='amnil-profile__eyebrow'>My Profile</div>
            <h1 className='amnil-profile__name'>{name}</h1>
            {user?.data?.role?.name && (
              <span className='amnil-profile__role'>
                <SecurityScanOutlined />
                {user?.data?.role?.name}
              </span>
            )}
          </div>
        </div>

        <button
          className='amnil-profile__btn amnil-profile__btn--primary amnil-profile__btn--lg'
          data-bs-toggle='modal'
          data-bs-target='#kt_modal_offer_a_deal'
          onClick={() => setOpen(true)}
          data-cy='change-pass-button'
        >
          Change Password
        </button>

        {contactChips.length > 0 && (
          <ul className='amnil-profile__chips'>
            {contactChips.map((chip) => (
              <li key={chip.key}>
                {chip.icon}
                <span>{chip.value}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Personal information ── */}
      <section className='amnil-profile__card'>
        <header className='amnil-profile__card-head'>
          <h2 className='amnil-profile__card-title'>Personal Information</h2>
          <p className='amnil-profile__card-sub'>
            Only your mobile number can be changed here. Contact your administrator for other
            updates.
          </p>
        </header>
        <div className='amnil-profile__card-body'>
          <Formik
            initialValues={{}}
            validationSchema={FORM_VALIDATION}
            onSubmit={(values: any) => {
              let formData = {
                mobile_number: values?.mobile_number,
              }
              dispatch(userRedux.actions.updateSingleUser(formData, user?.data?.id))
              // dispatch(user.actions.addUsers(values))
            }}
          >
            {({
              isSubmitting,
              touched,
              handleChange,
              handleSubmit,
              errors,
              values,
              setFieldValue,
            }: any) => {
              useEffect(() => {
                if (!isEmpty(user?.data)) {
                  const fields = ['name', 'userName', 'email', 'mobile_number']
                  fields.forEach((field) => setFieldValue(field, user?.data[field], false))
                }
              }, [])
              const handleCancel = () => {
                setFieldValue('mobile_number', user?.data?.mobile_number)
              }
              return (
                <Form>
                  <div className='row'>
                    <div className='col-md-6 col-12'>
                      <FormTextBox
                        labelClassName='col-12'
                        containerClassName='col-12'
                        type='text'
                        placeholder='Name'
                        label='Name'
                        name='name'
                        disabled={!isEmpty(user?.data) ? true : false}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required={true}
                      />
                    </div>
                    <div className='col-md-6 col-12'>
                      <FormTextBox
                        labelClassName='col-12'
                        containerClassName='col-12'
                        type='text'
                        placeholder='Username'
                        label='Username'
                        name='userName'
                        disabled={!isEmpty(user?.data) ? true : false}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required={true}
                      />
                    </div>
                    <div className='col-md-6 col-12'>
                      <FormTextBox
                        labelClassName='col-12'
                        containerClassName='col-12'
                        type='email'
                        placeholder='Email'
                        name='email'
                        label='Email'
                        disabled={!isEmpty(user?.data) ? true : false}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required={true}
                      />
                    </div>

                    <div className='col-md-6 col-12'>
                      <FormTextBox
                        labelClassName='col-12'
                        containerClassName='col-12'
                        type='text'
                        placeholder='Enter Mobile Number'
                        label='Mobile Number'
                        name='mobile_number'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required={true}
                      />
                    </div>
                  </div>

                  <div className='amnil-profile__actions'>
                    <button
                      type='submit'
                      disabled={
                        user?.data?.mobile_number !== values?.mobile_number ? false : true
                      }
                      className='amnil-profile__btn amnil-profile__btn--primary'
                      data-cy='modal-submit'
                    >
                      Save
                    </button>
                    <button
                      type='button'
                      onClick={handleCancel}
                      disabled={
                        user?.data?.mobile_number !== values?.mobile_number ? false : true
                      }
                      className='amnil-profile__btn amnil-profile__btn--ghost'
                      data-cy='modal-cancel'
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </section>

      {/* ── Appearance ── */}
      <section className='amnil-profile__card'>
        <header className='amnil-profile__card-head'>
          <h2 className='amnil-profile__card-title'>Appearance</h2>
        </header>
        <div className='amnil-profile__card-body amnil-profile__setting'>
          <div className='d-flex flex-column'>
            <span className='amnil-profile__setting-label'>Theme</span>
            <span className='amnil-profile__setting-hint'>
              Choose light or dark mode for this browser.
            </span>
          </div>
          <ThemeModeSelector className='' />
        </div>
      </section>

      {open && <ChangePassword open={open} userId={user?.data?.id} handleClose={handleClose} />}
    </div>
  )
}

export { ProfileHeader }
